import React from 'react';
import { motion } from 'framer-motion';
import { Coffee, Sun, Moon } from 'lucide-react';
export function Chapter4() {
  return <section id="capitulo-4" className="min-h-screen py-24 px-6 lg:px-16 bg-white relative" data-id="element-134">
      <div className="max-w-4xl mx-auto" data-id="element-135">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-16" data-id="element-136">
          <h2 className="text-gold font-medium tracking-widest uppercase text-sm mb-4" data-id="element-137">
            Capítulo 4
          </h2>
          <h3 className="font-serif text-4xl lg:text-5xl text-darkBrown font-bold mb-6 leading-tight" data-id="element-138">
            El Protocolo de 14 Días
          </h3>
          <p className="text-xl text-darkBrown/80 text-balance" data-id="element-139">
            No necesitas contar calorías. Solo necesitas seguir la Regla del
            Plato Dorado y respetar los ritmos naturales de tu digestión.
          </p>
        </motion.div>

        {/* Plate Rule Diagram */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-lightGold rounded-3xl p-8 lg:p-12 mb-16 flex flex-col md:flex-row items-center gap-10 border border-gold/20" data-id="element-140">
          <div className="w-full md:w-1/2 flex justify-center" data-id="element-141">
            <div className="relative w-64 h-64 rounded-full border-8 border-white shadow-xl overflow-hidden flex flex-wrap" data-id="element-142">
              {/* 50% Veggies */}
              <div className="w-1/2 h-full bg-green-600/80 flex items-center justify-center p-4 text-center" data-id="element-143">
                <span className="text-white font-serif font-bold text-xl drop-shadow-md" data-id="element-144">
                  50%
                  <br data-id="element-145" />
                  <span className="text-sm font-sans font-normal" data-id="element-146">
                    Vegetales Fibrosos
                  </span>
                </span>
              </div>
              <div className="w-1/2 h-full flex flex-col" data-id="element-147">
                {/* 25% Protein */}
                <div className="w-full h-1/2 bg-amber-700/80 flex items-center justify-center p-2 text-center border-b-4 border-white" data-id="element-148">
                  <span className="text-white font-serif font-bold text-lg drop-shadow-md" data-id="element-149">
                    25%
                    <br data-id="element-150" />
                    <span className="text-xs font-sans font-normal" data-id="element-151">
                      Proteína Limpia
                    </span>
                  </span>
                </div>
                {/* 25% Fats */}
                <div className="w-full h-1/2 bg-gold/90 flex items-center justify-center p-2 text-center" data-id="element-152">
                  <span className="text-white font-serif font-bold text-lg drop-shadow-md" data-id="element-153">
                    25%
                    <br data-id="element-154" />
                    <span className="text-xs font-sans font-normal" data-id="element-155">
                      Grasas Sanas
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full md:w-1/2 space-y-4" data-id="element-156">
            <h4 className="font-serif text-3xl font-bold text-darkBrown" data-id="element-157">
              La Regla del Plato
            </h4>
            <p className="text-darkBrown/80" data-id="element-158">
              Esta proporción estabiliza tu insulina y apaga la inflamación en
              cada comida.
            </p>
            <ul className="space-y-3 mt-4" data-id="element-159">
              <li className="flex items-start gap-2" data-id="element-160">
                <div className="w-3 h-3 rounded-full bg-green-600 mt-1.5" data-id="element-161"></div>
                <span className="text-darkBrown/80" data-id="element-162">
                  <strong data-id="element-163">Vegetales:</strong> Espinaca, brócoli, espárragos,
                  calabacín. Aportan fibra y antioxidantes.
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-164">
                <div className="w-3 h-3 rounded-full bg-amber-700 mt-1.5" data-id="element-165"></div>
                <span className="text-darkBrown/80" data-id="element-166">
                  <strong data-id="element-167">Proteína:</strong> Huevos, pescado, pollo, pavo.
                  Repara tejidos y mantiene masa muscular.
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-168">
                <div className="w-3 h-3 rounded-full bg-gold mt-1.5" data-id="element-169"></div>
                <span className="text-darkBrown/80" data-id="element-170">
                  <strong data-id="element-171">Grasas:</strong> Aguacate, aceite de oliva, nueces.
                  Esenciales para las hormonas y saciedad.
                </span>
              </li>
            </ul>
          </div>
        </motion.div>

        {/* Sample Menu */}
        <h4 className="font-serif text-3xl font-bold text-darkBrown mb-8 text-center" data-id="element-172">
          Menú de Ejemplo (Día 1)
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-173">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-cream/50 p-6 rounded-2xl border border-gold/10" data-id="element-174">
            <div className="flex items-center gap-3 mb-4 text-gold" data-id="element-175">
              <Coffee className="w-6 h-6" data-id="element-176" />
              <h5 className="font-serif text-xl font-bold text-darkBrown" data-id="element-177">
                Desayuno
              </h5>
            </div>
            <p className="font-medium text-darkBrown mb-2" data-id="element-178">Revuelto Dorado</p>
            <p className="text-darkBrown/70 text-sm" data-id="element-179">
              2 huevos revueltos cocinados en ghee, con espinacas frescas y una
              pizca de cúrcuma. Acompañado de 1/4 de aguacate.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="bg-cream/50 p-6 rounded-2xl border border-gold/10" data-id="element-180">
            <div className="flex items-center gap-3 mb-4 text-gold" data-id="element-181">
              <Sun className="w-6 h-6" data-id="element-182" />
              <h5 className="font-serif text-xl font-bold text-darkBrown" data-id="element-183">
                Almuerzo
              </h5>
            </div>
            <p className="font-medium text-darkBrown mb-2" data-id="element-184">
              Ensalada de Salmón
            </p>
            <p className="text-darkBrown/70 text-sm" data-id="element-185">
              Filete de salmón al horno sobre cama de hojas verdes, pepino,
              aceitunas y aderezo generoso de aceite de oliva y limón.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-cream/50 p-6 rounded-2xl border border-gold/10" data-id="element-186">
            <div className="flex items-center gap-3 mb-4 text-gold" data-id="element-187">
              <Moon className="w-6 h-6" data-id="element-188" />
              <h5 className="font-serif text-xl font-bold text-darkBrown" data-id="element-189">
                Cena
              </h5>
            </div>
            <p className="font-medium text-darkBrown mb-2" data-id="element-190">
              Crema Reconfortante
            </p>
            <p className="text-darkBrown/70 text-sm" data-id="element-191">
              Crema de calabaza y jengibre (hecha con caldo de huesos),
              acompañada de pechuga de pollo desmenuzada.
            </p>
          </motion.div>
        </div>

        <div className="mt-12 p-6 bg-darkBrown text-cream rounded-2xl text-center" data-id="element-192">
          <h5 className="font-serif text-xl font-bold text-gold mb-2" data-id="element-193">
            El Poder de la Cena Temprana
          </h5>
          <p className="text-cream/80 text-sm max-w-2xl mx-auto" data-id="element-194">
            Intenta cenar al menos 3 horas antes de dormir. La digestión
            nocturna eleva la temperatura corporal y el cortisol, impidiendo que
            tu cuerpo entre en la fase profunda de sueño donde ocurre la quema
            de grasa y la reparación celular.
          </p>
        </div>
      </div>
    </section>;
}